import $ from 'jquery';
import _ from 'underscore';
import Backbone from 'backbone';
import CacheManager from '@component/common/src/util/CacheManager';
import Cookie from '@component/common/src/util/Cookie';
import ClientSideStorage from '@component/common/src/util/ClientSideStorage';
import ObjectUtil from '@component/common/src/util/ObjectUtil';
import Globals from '@component/common/src/util/Globals';
import Logger from '@component/common/src/util/Logger';
import ExperimentationSDK from '@component/experiment';
import PageApp from 'pageApp';
import qbActions from '../components/QuickBagActions';
import TrueValueMbMoney from '../components/TrueValueMbMoney';
import CaptureTiming from '../util/CaptureTiming';
import ServiceUrl from '../util/ServiceUrl';
import setClickActioninQuickBagButton from '../util/setClickActioninQuickBagButton';

/* istanbul ignore next */
const Channel = PageApp.channel ? PageApp.channel : PageApp.default.channel;

const currentFetch = {
  isOn: false,
  promise: null,
};
let metaKillSwitchValues = null;

const QuickBagModel = Backbone.Model.extend({

  sessionKey: 'qbRest',
  cacheTimeExpiry: '15',
  isProcessing: false,
  properties: {},
  urlRoot: ServiceUrl.getUrl,

  // ###Method - getData()
  // This method ensures data is most updated. It will check if the data stored in the session storage is invalid then it will make a fetch call
  //
  // > returns
  // >
  // + A promise
  async getData() {
    Logger.log('getdata call');
    const deferred = $.Deferred();
    this.onlineUID = Cookie.get('macys_online_uid');
    if (!this.onlineUID) {
      currentFetch.promise = deferred.promise();
      // this is to handle showing error message if the macys_online_uid cookie is not available
      // By setting this it will show zero bag Items without making the service call
      this.set({
        meta: {
          totalQuantity: 0,
        },
        bagItems: [],
      });
      // when user comes to page first time
      await this.experimentWithQuickBag(true);
      // this is to reset the bag Item count to zero since the bagguid is not available
      Channel.trigger(qbActions.UPDATE_COUNT, this.attributes.meta.totalQuantity);
      this.attributes.meta.isTaggingOnQuickBagEnabled = (metaKillSwitchValues || {}).isTaggingOnQuickBagEnabled;
      deferred.resolve({
        data: this.attributes,
        reFetched: false,
      });
    } else if (this.isCachedDataValid() === true) {
      currentFetch.promise = deferred.promise();
      const cachedData = CacheManager.getSession(this.sessionKey);
      if (!_.has(cachedData, 'srBalance')) {
        this.setStarRewardsCache(cachedData, this);
      } else if ((Cookie.get('SignedIn') === '0') && cachedData && cachedData.bagItems && cachedData.bagItems.length && cachedData.meta.miniBagSRBalanceEnabled) {
        const { srBalance, ...newCachedData } = cachedData;
        this.unset('srBalance', { silent: true });
        this.setCacheData(newCachedData);
      }
      // if data in cache is valid but model is empty. this can happen when a page is reloaded.
      // we set the model so that view can re-render itself.
      /* istanbul ignore next */
      this.set(this.getCachedData());
      const isSkipBagOnQuickBagEnabled = ObjectUtil.getChildProperty(this, 'attributes.meta.isSkipBagOnQuickBagEnabled');
      if (isSkipBagOnQuickBagEnabled) {
        await this.experimentWithQuickBag();
      }
      // this is to handle an edge case when 'my bag' link has a different count
      Channel.trigger(qbActions.UPDATE_COUNT, ObjectUtil.getChildProperty(this, 'attributes.meta.totalQuantity'));
      deferred.resolve({
        data: this.attributes,
        reFetched: false,
      });
    } else if (!currentFetch.isOn) {
      currentFetch.isOn = true;
      currentFetch.promise = deferred.promise();
      const self = this;
      this.fetch({
        cache: false,
        success: async (model, response) => {
          const isSkipBagOnQuickBagEnabled = ObjectUtil.getChildProperty(response, 'meta.isSkipBagOnQuickBagEnabled');
          if (isSkipBagOnQuickBagEnabled) {
            await this.experimentWithQuickBag();
          }
          currentFetch.isOn = false;
          currentFetch.promise = null;
          // unsetting the loadingError to ensure our model still doesn't indicate a fetch error
          // passing silent true so that "change" event is not triggered and view is not rendered
          this.unset('loadingError', {
            silent: true,
          });
          // sets the session with new response
          this.setCacheData(response);
          this.properties = response.meta.properties;
          // update the item count next to "my bag" link
          // this is to handle an edge case when 'my bag' link has a different count
          Channel.trigger(qbActions.UPDATE_COUNT, ObjectUtil.getChildProperty(response, 'meta.totalQuantity'));
          this.setStarRewardsCache(response, self);
          deferred.resolve({
            data: response,
            reFetched: true,
          });
        },
        error: (model) => {
          model.set({
            loadingError: true,
            bagItems: [],
          });
          currentFetch.isOn = false;
          currentFetch.promise = null;
          this.expireCache();
          deferred.reject();
        },
      });
    }
    const qVEndTime = window.performance.now();
    if (window.properties && window.properties.quickBagPerformanceInstrumentationEnabled === true) {
      CaptureTiming.saveTimeData({
        appName: 'SPO_MCOM_HEADER_QUICKBAG',
        name: 'Header_QuickBag',
        timeStart: Globals.getValue('qVStartTime'),
        timeEnd: qVEndTime,
        description: 'Start and stop times for display of quickbag overlay',
        logging: true,
      });
    }
    return currentFetch.promise;
  },
  quickCheckout() {
    const fingerprintHeader = {};

    const login365Enabled = ObjectUtil.getChildProperty(this, 'attributes.meta.login365Enabled');
    if (login365Enabled) {
      fingerprintHeader['X-Macys-DeviceFingerPrint'] = ClientSideStorage.getPersistent('login365FingerPrint');
    }
    const bagGuid = Cookie.get('macys_bagguid');
    const url = `/xapi/bag/v1/${bagGuid}/expressCheckout`;
    return $.ajax({
      url,
      type: 'POST',
      headers: fingerprintHeader,
    });
  },
  setExperimentationAction(selectedRecipe) {
    if (selectedRecipe.indexOf('959-21') !== -1) {
      setClickActioninQuickBagButton();
    }
    this.set('isSkipBagOnQuickBagEnabled', true);
  },
  getKillSWitchFromMeta() {
    return new Promise((resolve) => {
      $.ajax({
        type: 'GET',
        url: '/xapi/bag/v1/meta',
      }).done((response) => {
        metaKillSwitchValues = ObjectUtil.getChildProperty(response, 'MetaInfo.killswitches');
        resolve((metaKillSwitchValues || {}).isSkipBagOnQuickBagEnabled);
      }).fail(() => {
        resolve(false);
      });
    });
  },
  experimentWithQuickBag(checkKs) {
    try {
      // checkKs is used when no items in bag. Whether
      const expRecipeIDs = ['959-20', '959-21', '959-11', '959-22'];
      return new Promise((resolve) => {
        Promise.all([ExperimentationSDK.getSelectedRecipesForExperiment(expRecipeIDs),
          ExperimentationSDK.getAllSelectedRecipes()]).then(([seedExperiments = [], sessionKeyExperiments = []]) => {
          const selectedRecipe = seedExperiments.concat(sessionKeyExperiments);
          if (selectedRecipe.indexOf('959-21') !== -1 || selectedRecipe.indexOf('959-22') !== -1) {
            // when user come to page first time , no api call to quick bag is made so ks wont be present
            // if experimentation is on, we make the api call to determine if ks is on - this willl reduce the api call if experimentation is off
            // when user has added first bag item ckeckks will always be undefined , as macys_online_uid is added to cookies
            // Added !this.metaKillSwitchValues check to avoid multiple calls to the meat API
            if (checkKs && !metaKillSwitchValues) {
              this.getKillSWitchFromMeta().then((isSkipBagOnQuickBagEnabled) => {
                if (isSkipBagOnQuickBagEnabled) {
                  this.setExperimentationAction(selectedRecipe);
                }
                resolve();
              });
            } else if (!checkKs || (checkKs && (metaKillSwitchValues || {}).isSkipBagOnQuickBagEnabled)) {
              this.setExperimentationAction(selectedRecipe);
              resolve();
            } else {
              resolve();
            }
          } else if (checkKs) {
            if (!metaKillSwitchValues) {
              this.getKillSWitchFromMeta().then(() => {
                resolve();
              }).catch(() => {
                resolve();
              });
            } else {
              resolve();
            }
          } else {
            this.set('isSkipBagOnQuickBagEnabled', false);
            resolve();
          }
        }).catch(() => {
          resolve();
        });
      });
    } catch (error) {
      return Promise.resolve();
    }
  },
  setStarRewardsCache(response, self) {
    if ((Cookie.get('SignedIn') === '1' || Cookie.get('SignedIn') === 1) && response.bagItems && response.bagItems.length && response.meta.miniBagSRBalanceEnabled) {
      if (window.localStorage) {
        const srBalance = {};
        if (self.onlineUID) {
          const hnfStarRewards = JSON.parse(localStorage.getItem(`hnfsr_${self.onlineUID}`));
          if (hnfStarRewards) {
            const totalStarRewardCardsBalance = ObjectUtil.getChildProperty(hnfStarRewards, 'rewardsInfo.totalStarRewardCardsBalance');
            srBalance.isHnfStarRewards = hnfStarRewards;
            srBalance.nonZeroBalance = !((!totalStarRewardCardsBalance || totalStarRewardCardsBalance === '0.00'));
            srBalance.sumOfSRCardsBalance = totalStarRewardCardsBalance ? `$${totalStarRewardCardsBalance}` : '$0.00';
            self.set('srBalance', srBalance);
          } else {
            srBalance.error = 'NO_LOYALTY_NUMBER';
            srBalance.nonZeroBalance = false;
            srBalance.sumOfSRCardsBalance = '$0.00';
            self.set('srBalance', srBalance);
          }
        } else { self.set('srBalance', ''); }
      } else {
        self.set('srBalance', '');
      }
    }
  },

  // ###Method - isCachedDataValid()
  // method to check whether QuickBag data in the session valid or not
  // > returns
  // >
  // + Boolean
  isCachedDataValid() {
    const cachedData = CacheManager.getSession(this.sessionKey);

    if (cachedData !== null && this.itemCountValidator() !== false) {
      if (cachedData.shippingCountry !== Cookie.get('shippingCountry')) {
        this.expireCache();
        return false;
      }
      return true;
    }
    this.expireCache();
    return false;
  },

  // ###Method - getCachedData()
  // method to retrieve QuickBag data from the session
  //
  // > returns
  // >
  // + DataModel or Null
  getCachedData() {
    const cachedData = CacheManager.getSession(this.sessionKey);

    /* istanbul ignore else */
    if (cachedData !== null || this.itemCountValidator() === false) {
      return cachedData;
    }
    return null;
  },

  // ###Method - itemCountValidator()
  // Method to validate number of items in cookie and number of items session storage is equal or not.
  // > returns
  // >
  // + Boolean
  itemCountValidator() {
    const cachedItemCount = this.getCachedItemCount();
    const cookieItemCount = parseInt(Cookie.get('CartItem', 'GCs'), 10);

    // if count in cookie is not a number & count in session storage is 0 return true
    // if count in cookie is matching count in session storage return true
    if (((Number.isNaN(cookieItemCount)) && (cachedItemCount === 0)) || (cookieItemCount === cachedItemCount)) {
      return true;
    }
    return false;
  },

  // ###Method - getCachedItemCount()
  // Method to retrieve number of bag items in the session storage
  // > returns
  // >
  // + Number - Number of products in QuickBag
  getCachedItemCount() {
    const currentCachedResponse = CacheManager.getSession(this.sessionKey);

    if (currentCachedResponse && currentCachedResponse !== null) {
      return currentCachedResponse.meta.totalQuantity;
    }
    return -1;
  },

  // ###Method - expireCache()  method to remove QuickBag session if exists
  // > returns
  // >
  // + (none)
  expireCache() {
    CacheManager.removeSession(this.sessionKey);
  },

  // ###Method - setCacheData()
  // method to set QuickBag model in session storage
  // > parameters
  // >
  // + *model* - Model data from the response
  // > returns
  // >
  // + (none)
  setCacheData(_model) {
    const model = _model;
    model.shippingCountry = Cookie.get('shippingCountry');
    CacheManager.setSession(this.sessionKey, model, this.cacheTimeExpiry);
  },

  // ###Method - Overriding Backbone.model.parse to do update discount and total price amounts and to form banner for QuickBag
  // > parameters
  // >
  // + *attributes* - Attributes from the response
  // > returns
  // >
  // + (Object) - the massaged response
  parse(_attrs) {
    const attrs = _attrs;
    let promoPool;
    let adLinks;
    let promoText;
    let poolImgSrc;
    let adMap;
    let adPoolItems;
    const { mbmoney } = attrs.meta;

    _.map(attrs.meta.price, (_price) => {
      const price = _price;
      if (price.type === 'discount') {
        price.label = price.label.replace(/\[PRICE\]/g, () => `<strong>${price.formattedValue[0]}</strong>`);
      } else if (price.type === 'total') {
        price.label = price.label.replace(/(.*)?\s(\[PRICE\])/g, (matched, m1) => `<a href='${attrs.meta.properties.baseHost}${attrs.meta.bagUrl}'>${m1}</a> <strong>${price.formattedValue[0]}</strong>`);
      }
      return price;
    });

    if (attrs.media) {
      promoPool = attrs.media.BAG_AD_POOL;
      if (promoPool && ObjectUtil.hasChildProperty(promoPool, 'items.0.adLinks')) {
        [adPoolItems] = promoPool.items;
        [adLinks] = adPoolItems.adLinks;
        promoText = adPoolItems.text || '';
        poolImgSrc = `${attrs.meta.properties.assetsHost}/dyn_img/site_ads/${adPoolItems.fileName}`;
        adMap = `<a href=${ObjectUtil.getChildProperty(adLinks, 'url')}><img src="${poolImgSrc}" alt="${promoText}"/></a>`;
        attrs.media.adMap = adMap;
      }
    }

    if (mbmoney) {
      const { estimatedGetRewardMoney } = mbmoney;
      const { spendMore } = mbmoney;
      mbmoney.showEstimatedAndSpendMore = (estimatedGetRewardMoney > 0 && spendMore > 0) || (estimatedGetRewardMoney === 0 && spendMore > 0);
      mbmoney.showOnlyEstimated = estimatedGetRewardMoney > 0 && spendMore === 0;
      mbmoney.estimatedGetRewardMoneyFormatted = mbmoney.estimatedGetRewardMoneyFormatted ? mbmoney.estimatedGetRewardMoneyFormatted : `$${mbmoney.estimatedGetRewardMoney.toFixed(2)}`;
    }
    attrs.meta.mbmoney = mbmoney;
    attrs.showMacysMoney = TrueValueMbMoney.isTreatment();
    return attrs;
  },
});

export default QuickBagModel;
